/**
 * World state and the fixed-step advance. The sim runs at 20 Hz (50 ms ticks),
 * is pure with respect to the outside world (no DOM, no clock, no network, no
 * unseeded randomness), and must stay deterministic: same seed plus same input
 * stream produces the same `hashWorld` on every machine. If a change alters
 * simulation output, bump `SIM_VERSION` — replays and leaderboard entries are
 * invalidated by the bump, so it is a deliberate decision.
 *
 * M0 movement is a free-fly camera with no collision: `stepWorld` integrates
 * the look angles and move intents at a fixed speed. Terrain is generated on
 * demand into `store` and never mutated by the sim; player changes live in the
 * `edits` overlay so a save is just seed + edits.
 */

import { BLOCK } from "../data/blocks";
import { WORLD_CEILING, WORLD_FLOOR } from "../data/worldgen";
import { ChunkStore, blockIndex, chunkCoord, localCoord } from "./chunk";
import { createRng, type Rng } from "./rng";
import { generateChunk, surfaceHeight } from "./worldgen";

/** Bump when a change alters simulation output for the same seed and inputs. */
export const SIM_VERSION = 1;

/** Fixed timestep: one `stepWorld` call per 50 ms tick (20 Hz). */
export const TICK_MS = 50;

/** Fly speed: 20 blocks per second = 1 block per tick at full intent. */
const BLOCKS_PER_TICK = 20 * (TICK_MS / 1000);

/**
 * One tick of player input. Move axes are -1/0/1 intents: moveZ forward along
 * the look direction, moveX strafe right, moveY straight up. yaw and pitch are
 * absolute look angles in radians; yaw = 0 faces -z, positive pitch looks up.
 */
export interface InputFrame {
  moveX: number;
  moveY: number;
  moveZ: number;
  yaw: number;
  pitch: number;
}

export const NO_INPUT: InputFrame = { moveX: 0, moveY: 0, moveZ: 0, yaw: 0, pitch: 0 };

export interface World {
  seed: number;
  tick: number;
  position: { x: number; y: number; z: number };
  yaw: number;
  pitch: number;
  /** M0 has no death; the field is part of the hashed run contract. */
  alive: boolean;
  /** Block edits overlay, "x,y,z" → block id. Terrain bytes stay pristine. */
  edits: Map<string, number>;
  /** Counters sent to the server for plausibility checks. Keep the key set stable. */
  stats: { maxDepth: number; edits: number };
  rng: Rng;
  store: ChunkStore;
}

/** Spawn column. On the heightmap, near the origin, fixed so spawns are reproducible. */
const SPAWN_X = 8;
const SPAWN_Z = 8;

export function createWorld(seed: number): World {
  return {
    seed,
    tick: 0,
    // Feet one block above the ground block, facing -z (yaw 0).
    position: { x: SPAWN_X, y: surfaceHeight(seed, SPAWN_X, SPAWN_Z) + 1, z: SPAWN_Z },
    yaw: 0,
    pitch: 0,
    alive: true,
    edits: new Map(),
    stats: { maxDepth: 0, edits: 0 },
    rng: createRng(seed),
    store: new ChunkStore(),
  };
}

/**
 * Advance one fixed step. Free-fly movement: forward intent follows the full
 * 3D look direction, strafe and vertical intents are orthogonal to it, and the
 * combined intent is normalised when longer than 1 so diagonals are not faster.
 * Mutates the world in place — this runs every tick and must not allocate.
 */
export function stepWorld(world: World, input: InputFrame): void {
  if (!world.alive) return;

  world.tick++;
  world.yaw = input.yaw;
  world.pitch = input.pitch;

  const sinYaw = Math.sin(input.yaw);
  const cosYaw = Math.cos(input.yaw);
  const sinPitch = Math.sin(input.pitch);
  const cosPitch = Math.cos(input.pitch);

  // Look direction at yaw 0 / pitch 0 is (0, 0, -1): facing -z.
  const forwardX = -sinYaw * cosPitch;
  const forwardY = sinPitch;
  const forwardZ = -cosYaw * cosPitch;
  const rightX = cosYaw;
  const rightZ = -sinYaw;

  let moveX = forwardX * input.moveZ + rightX * input.moveX;
  let moveY = forwardY * input.moveZ + input.moveY;
  let moveZ = forwardZ * input.moveZ + rightZ * input.moveX;

  // Math.sqrt is IEEE-exact across engines (unlike Math.hypot), so the
  // normalisation cannot drift between browsers or Node versions.
  const length = Math.sqrt(moveX * moveX + moveY * moveY + moveZ * moveZ);
  if (length > 1) {
    moveX /= length;
    moveY /= length;
    moveZ /= length;
  }

  world.position.x += moveX * BLOCKS_PER_TICK;
  world.position.y += moveY * BLOCKS_PER_TICK;
  world.position.z += moveZ * BLOCKS_PER_TICK;

  const depth = Math.floor(-world.position.y);
  if (depth > world.stats.maxDepth) world.stats.maxDepth = depth;
}

/** Edit-map key for a block position. */
export function blockKey(x: number, y: number, z: number): string {
  return `${x},${y},${z}`;
}

/**
 * Read a block at world coordinates: edits overlay first, then generated
 * terrain, generating the chunk into the store on demand. Above the ceiling
 * reads as air and below the floor as bedrock without generating anything.
 */
export function getBlock(world: World, x: number, y: number, z: number): number {
  const edit = world.edits.get(blockKey(x, y, z));
  if (edit !== undefined) return edit;
  if (y > WORLD_CEILING) return BLOCK.AIR;
  if (y < WORLD_FLOOR) return BLOCK.BEDROCK;

  const cx = chunkCoord(x);
  const cy = chunkCoord(y);
  const cz = chunkCoord(z);
  let chunk = world.store.getChunk(cx, cy, cz);
  if (!chunk) {
    chunk = generateChunk(world.seed, cx, cy, cz);
    world.store.setChunk(chunk);
  }
  return chunk.blocks[blockIndex(localCoord(x), localCoord(y), localCoord(z))] ?? BLOCK.AIR;
}

/**
 * Record a block change in the edits overlay. Mining is M1; this exists now so
 * tests (and later the input handler) can write edits the save system persists.
 */
export function applyEdit(world: World, x: number, y: number, z: number, blockId: number): void {
  world.edits.set(blockKey(x, y, z), blockId);
  world.stats.edits++;
}

/**
 * Order-independent state fingerprint. The replay test compares this, so it
 * covers everything that defines a run and nothing that does not: tick, rounded
 * kinematics (trig noise below 1e-3 is rendering's problem, not the sim's),
 * alive, edits sorted by key, stats sorted by key, and the rng state.
 */
export function hashWorld(world: World): string {
  const parts: Array<string | number> = [
    world.tick,
    Math.round(world.position.x * 1000),
    Math.round(world.position.y * 1000),
    Math.round(world.position.z * 1000),
    Math.round(world.yaw * 1000),
    Math.round(world.pitch * 1000),
    world.alive ? 1 : 0,
  ];
  const editKeys = [...world.edits.keys()].sort();
  for (const key of editKeys) parts.push(`${key}=${world.edits.get(key) ?? 0}`);
  const statKeys = Object.keys(world.stats).sort() as Array<keyof World["stats"]>;
  for (const key of statKeys) parts.push(`${key}:${world.stats[key]}`);
  parts.push(world.rng.serialize());
  return fnv1a(parts.join("|"));
}

/**
 * Fingerprint of a recorded input stream. `RunSummary.inputHash` must be
 * "produced by the sim, not the renderer", so the function lives here even
 * though only `main.ts` calls it. Look angles are rounded to 1e-3 like
 * `hashWorld` does: trig noise below that is rendering's concern, not the
 * replay checker's. Purely additive — does not alter simulation output.
 */
export function hashInputs(inputs: readonly InputFrame[]): string {
  const parts: string[] = [];
  for (const input of inputs) {
    parts.push(
      `${input.moveX},${input.moveY},${input.moveZ},${Math.round(input.yaw * 1000)},${Math.round(input.pitch * 1000)}`,
    );
  }
  return fnv1a(parts.join("|"));
}

export function fnv1a(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}
